import React, { useEffect } from 'react';
import { ActionIcon, Modal, Textarea, TextInput, Radio, Button, Stack, Text, Group, Tooltip } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconHeadset } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useSessionProvider } from '../../../provider/sessionContext';
import { useSubmitSupportRequest } from '../../../entities/SupportRequest/mutations/useSubmitSupportRequest';
import { useGetSupportContactInfo } from '../../../entities/SupportRequest/queries/useGetSupportContactInfo';
import { PostHogService } from '../../../services/posthogService';

export const SupportRequestButton: React.FC = () => {
    const { t } = useTranslation();
    const { currentUser } = useSessionProvider();
    const [opened, { open, close }] = useDisclosure(false);
    const isLoggedIn = !!currentUser;

    const { data: contactInfo } = useGetSupportContactInfo(opened && isLoggedIn);
    const submitMutation = useSubmitSupportRequest();

    const schema = z.object({
        name: isLoggedIn ? z.string().optional() : z.string().min(1, t('formError.required')),
        message: z.string().min(1, t('formError.required')),
        preferredContactMethod: z.string().min(1, t('formError.required')),
        contactValue: z.string().min(1, t('formError.required')),
    });

    const form = useForm({
        initialValues: {
            name: '',
            message: '',
            preferredContactMethod: 'email',
            contactValue: '',
        },
        validate: zodResolver(schema),
    });

    useEffect(() => {
        if (contactInfo && isLoggedIn) 
            form.setFieldValue('contactValue', contactInfo.email || '');
        
    }, [contactInfo]);

    const handleContactMethodChange = (value: string | null) => {
        form.setFieldValue('preferredContactMethod', value || 'email');
        if (contactInfo && isLoggedIn) {
            if (value === 'phone') 
                form.setFieldValue('contactValue', contactInfo.phoneNumber || '');
             else 
                form.setFieldValue('contactValue', contactInfo.email || '');
            
        } else 
            form.setFieldValue('contactValue', '');
        
    };

    const handleSubmit = (values: typeof form.values) => {
        const distinctId = PostHogService.getDistinctId();

        submitMutation.mutate(
            {
                message: values.message,
                preferredContactMethod: values.preferredContactMethod,
                contactValue: values.contactValue,
                postHogDistinctId: distinctId,
                name: isLoggedIn ? undefined : values.name,
                currentPage: window.location.pathname,
            },
            {
                onSuccess: () => {
                    PostHogService.capture('support_request_submitted', {
                        messagePreview: values.message.substring(0, 100),
                        contactMethod: values.preferredContactMethod,
                        isAuthenticated: isLoggedIn,
                    });
                    form.reset();
                    close();
                },
            },
        );
    };

    return (
        <>
            <Tooltip label={t('supportRequest.tooltip')} position="left" withArrow>
                <ActionIcon
                    variant="filled"
                    size="xl"
                    radius="xl"
                    color="cyan"
                    onClick={open}
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 10000,
                    }}
                >
                    <IconHeadset size={24} />
                </ActionIcon>
            </Tooltip>

            <Modal opened={opened} onClose={close} title={t('supportRequest.title')} size="md" zIndex={10000}>
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            {t('supportRequest.description')}
                        </Text>

                        {!isLoggedIn && (
                            <TextInput
                                label={t('supportRequest.name')}
                                placeholder={t('supportRequest.namePlaceholder')}
                                withAsterisk
                                {...form.getInputProps('name')}
                            />
                        )}

                        <Textarea
                            label={t('supportRequest.message')}
                            placeholder={t('supportRequest.messagePlaceholder')}
                            minRows={4}
                            withAsterisk
                            {...form.getInputProps('message')}
                        />

                        <Radio.Group
                            label={t('supportRequest.preferredContactMethod')}
                            value={form.values.preferredContactMethod}
                            onChange={handleContactMethodChange}
                        >
                            <Group mt="xs">
                                <Radio value="email" label={t('supportRequest.contactMethodEmail')} />
                                <Radio value="phone" label={t('supportRequest.contactMethodPhone')} />
                            </Group>
                        </Radio.Group>

                        <TextInput
                            label={t('supportRequest.contactValue')}
                            placeholder={
                                form.values.preferredContactMethod === 'phone'
                                    ? t('supportRequest.contactValuePhonePlaceholder')
                                    : t('supportRequest.contactValueEmailPlaceholder')
                            }
                            withAsterisk
                            {...form.getInputProps('contactValue')}
                        />

                        <Button type="submit" loading={submitMutation.isPending} fullWidth>
                            {t('supportRequest.submit')}
                        </Button>
                    </Stack>
                </form>
            </Modal>
        </>
    );
};
